import { useMemo } from 'react'

import { useAuth } from '@/auth/use-auth'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * The permission codes the customer endpoints authorise against.
 *
 * Three of them — update, status change, and the code that unmasks personal data — are not in the
 * generated catalogue in `src/lib/permissions.ts`, which the server emits from its own
 * definitions. They are therefore written here as plain strings rather than added to that file by
 * hand: an entry invented on this side would make the difference between the two sides invisible,
 * which is exactly what regenerating the catalogue is meant to expose.
 */
export const CUSTOMER_PERMISSIONS: Record<string, string> = {
  view: PERMISSIONS.customers.view,
  create: PERMISSIONS.customers.create,
  update: 'customers.update',
  kycApprove: PERMISSIONS.customers.kycApprove,
  statusChange: 'customers.status.change',
  piiView: 'customers.pii.view',
  /** Captures a passport photograph, signature specimen, or thumbprint specimen. */
  mediaCapture: 'customers.media.capture',
  /** Streams a captured specimen back — server-authorised, these are personal data under Act 843. */
  mediaView: 'customers.media.view',
  /**
   * Records a `CustomerVerification` before a transaction posts. Named `teller.*` on the server —
   * it identifies whose action this is (a teller's, at the counter) rather than which module's
   * code checks it, which is this one.
   */
  verificationPerform: 'teller.verification.perform',
  /** Lists recorded `Override` verifications for supervisor review. */
  verificationReview: 'teller.verification.review',
}

export interface CustomerPermissions {
  canView: boolean
  canCreate: boolean
  canUpdate: boolean
  canApproveKyc: boolean
  canChangeStatus: boolean
  /**
   * Whether unmasked personal data reaches this user at all. The masking happens server-side, so
   * this only decides what the screen *says* about the values it was given — it can never reveal
   * one, and a screen must not imply that a masked value is the whole of it.
   */
  canViewPii: boolean
  canCaptureMedia: boolean
  canViewMedia: boolean
  canPerformVerification: boolean
  canReviewVerificationOverrides: boolean
}

/**
 * What this user may do with a customer record. Presentation only: the server authorises every
 * request against the live catalogue, so a visible button is not an open door and a hidden one is
 * not a closed one.
 */
export function useCustomerPermissions(): CustomerPermissions {
  const { user } = useAuth()
  const permissions = user?.permissions

  return useMemo(() => {
    const holds = (code: string) => permissions?.some((held) => held === code) ?? false

    return {
      canView: holds(CUSTOMER_PERMISSIONS.view),
      canCreate: holds(CUSTOMER_PERMISSIONS.create),
      canUpdate: holds(CUSTOMER_PERMISSIONS.update),
      canApproveKyc: holds(CUSTOMER_PERMISSIONS.kycApprove),
      canChangeStatus: holds(CUSTOMER_PERMISSIONS.statusChange),
      canViewPii: holds(CUSTOMER_PERMISSIONS.piiView),
      canCaptureMedia: holds(CUSTOMER_PERMISSIONS.mediaCapture),
      canViewMedia: holds(CUSTOMER_PERMISSIONS.mediaView),
      canPerformVerification: holds(CUSTOMER_PERMISSIONS.verificationPerform),
      canReviewVerificationOverrides: holds(CUSTOMER_PERMISSIONS.verificationReview),
    }
  }, [permissions])
}
